import React, { useCallback, useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Alert, Box, Button, CircularProgress, Divider, Typography } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FormDialog from "components/Dialog/modal-popup";
import { AlertsProps } from "components/Alerts/data/Alerts.type";
import {
  _downloadOrganizationReductionMeasuresTemplate,
  _uploadOrganizationMeasure,
} from "middlewares/ReductionMeasure/ReductionMeasureApi";
import { ApiError } from "object-models/shared/api-error.model";
import { Utilities } from "utilities/utilities";
import { getFriendlyErrorMessage, reportUiError } from "services/ui-error-reporter";
import { Organization } from "./types";

interface OrganizationReductionMeasureDialogProps {
  open: boolean;
  organization: Organization | null;
  onClose: () => void;
  showSnackbar: (state: AlertsProps) => void;
}

const OrganizationReductionMeasureDialog: React.FC<OrganizationReductionMeasureDialogProps> = ({
  open,
  organization,
  onClose,
  showSnackbar,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setErrorMessage(null);
    }
  }, [open]);

  const handleDownloadTemplate = async () => {
    if (!organization) return;
    const organizationId = organization.orgId;
    try {
      setErrorMessage(null);
      setIsDownloading(true);
      const { blob, fileName } =
        await _downloadOrganizationReductionMeasuresTemplate(organizationId);
      if (blob) {
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, "");
        Utilities.triggerFileDownload(
          blob,
          fileName ?? `ReductionMeasures_Org_${organizationId}_${today}.xlsx`
        );
      }
    } catch (err) {
      setErrorMessage(getFriendlyErrorMessage(
        err,
        "Failed to download template. Please try again."
      ));
      reportUiError(err, {
        feature: "ReductionMeasures",
        action: "download organization reduction measure template",
        component: "OrganizationReductionMeasureDialog",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: { organizationId },
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleUpload = async () => {
    if (!organization || !selectedFile || isUploading) return;
    const organizationId = organization.orgId;
    try {
      setErrorMessage(null);
      setIsUploading(true);
      const formData = new FormData();
      formData.append("organizationId", String(organizationId));
      formData.append("fileName", selectedFile.name);
      formData.append("file", selectedFile, selectedFile.name);
      const response = await _uploadOrganizationMeasure(formData);
      if (
        !response ||
        response.success === false ||
        response.errorCode ||
        (response.failedRows ?? 0) > 0 ||
        (response.errors?.length ?? 0) > 0
      ) {
        throw new Error(
          response?.userMessage ||
          response?.message ||
          response?.errors?.map((error) => error.message).join(" ") ||
          "Failed to upload Excel. Please try again."
        );
      }
      showSnackbar({
        show: true,
        severity: "success",
        message: response.userMessage || response.message || "Excel file uploaded successfully!",
      });
      setSelectedFile(null);
      onClose();
    } catch (err) {
      const payload = err instanceof ApiError ? err.data : undefined;
      const userMessage =
        payload && typeof payload === "object" && "userMessage" in payload &&
        typeof payload.userMessage === "string" ? payload.userMessage : undefined;
      setErrorMessage(userMessage || getFriendlyErrorMessage(
        err,
        "Failed to upload Excel. Please try again."
      ));
      reportUiError(err, {
        feature: "ReductionMeasures",
        action: "upload organization reduction measures",
        component: "OrganizationReductionMeasureDialog",
        route: typeof window !== "undefined" ? window.location.pathname : undefined,
        extra: { organizationId },
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      setErrorMessage(null);
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
    },
    maxSize: 10 * 1024 * 1024,
    multiple: false,
    disabled: isUploading,
    onDropRejected: (rejections) => {
      setSelectedFile(null);
      setErrorMessage(rejections.flatMap(({ errors }) =>
        errors.map(({ message }) => message)
      ).join(" "));
    },
  });

  return (
    <FormDialog
      dialogOpen={open}
      hideDialogHandler={() => {
        if (!isUploading) onClose();
      }}
      dialogTitle={`Reduction Measures${organization ? ` - ${organization.orgName}` : ""}`}
      maxWidth="sm"
      childComponent={
        <Box data-testid="org-reduction-measure-dialog">
          {errorMessage && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              data-testid="org-reduction-measure-error-alert"
            >
              {errorMessage}
            </Alert>
          )}
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Download Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Download the Reduction Measures Excel template populated with every
            location of this organization.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            data-testid="download-org-reduction-template"
            startIcon={
              isDownloading ? (
                <CircularProgress size={14} />
              ) : (
                <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
              )
            }
            disabled={isDownloading || isUploading || !organization}
            onClick={handleDownloadTemplate}
            sx={{ textTransform: "none" }}
          >
            {isDownloading ? "Downloading..." : "Download Reduction Measure Template"}
          </Button>

          <Divider sx={{ my: 2.5 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Upload Reduction Measures
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Upload the completed template to add or update reduction measures for
            this organization's locations.
          </Typography>
          <Box
            {...getRootProps()}
            data-testid="org-reduction-dropzone"
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 2,
              p: 3,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
              "&:hover": { backgroundColor: "#f1f1f1" },
            }}
          >
            <input
              {...getInputProps()}
              aria-label="Reduction measures Excel file"
              disabled={isUploading}
            />
            <CloudUploadIcon sx={{ fontSize: 36, color: "orange" }} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              {selectedFile
                ? selectedFile.name
                : "Drag and drop your Excel file here, or click to browse."}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1, color: "gray" }}>
              Accepted formats: .xlsx, .xls | Max size: 10 MB
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              size="small"
              data-testid="upload-org-reduction-measures"
              disabled={!selectedFile || !organization || isUploading}
              onClick={handleUpload}
              startIcon={isUploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
              sx={{ textTransform: "none" }}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default OrganizationReductionMeasureDialog;
